import { DeprecatedCloseIcon as Close, IconButton, InputErrorMessage, useErrorId } from '@lokalise/louis';
import React, { useEffect, useRef, useState } from 'react';

import isEmail from '@app/utils/string/isEmail';

import unique from '../../utils/array/unique';
import EmailTag from './components/EmailTag';
import { ClearWrapper, Container, Input, InputContainer, Placeholder } from './styles';

type MultiEmailProps = {
    value: string[];
    error?: boolean | string | string[];
    onChange: (value: string[]) => void;
};

const MultiEmail = ({ value, error, onChange }: MultiEmailProps) => {
    const [internalEmails, setInternalEmails] = useState<string[]>(value);
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        setInternalEmails(value);
        setInputValue('');
    }, [value]);

    const findEmailAddress = (val: string, isEnter?: boolean) => {
        if (val === '' && isEnter) {
            return;
        }

        const validEmails: string[] = [];
        let newInputValue = '';
        const splitByComaOrSemicolon = /[ ,;]/g;

        const addEmailToValidEmails = (email: string) => {
            if (internalEmails.includes(email)) {
                return;
            }
            validEmails.push(email);
        };

        if (splitByComaOrSemicolon.test(val)) {
            const emailsFromString = val.split(splitByComaOrSemicolon).filter(Boolean);

            const uniqueEmails = unique(emailsFromString);

            uniqueEmails.forEach(addEmailToValidEmails);
        } else if (isEnter) {
            addEmailToValidEmails(val);
        } else {
            newInputValue = val;
        }

        setInternalEmails([...internalEmails, ...validEmails]);
        setInputValue(newInputValue);

        if (validEmails.length) {
            onChange([...internalEmails, ...validEmails]);
        }
    };

    const removeEmail = (index: number) => {
        const newInternalEmails = [...internalEmails.slice(0, index), ...internalEmails.slice(index + 1)];

        setInternalEmails(newInternalEmails);

        onChange(newInternalEmails);
    };

    const empty = inputValue === '' && internalEmails.length === 0;

    const errorId = useErrorId();

    const inputField = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        const timeout = setTimeout(() => {
            inputField.current?.focus();
        });

        return () => clearTimeout(timeout);
    }, []);

    return (
        <Container>
            <InputContainer error={!!error}>
                {empty ? <Placeholder>E-mails, comma separated</Placeholder> : null}
                {internalEmails.map((email, index) => (
                    <EmailTag key={email} valid={isEmail(email)} email={email} onClose={() => removeEmail(index)} />
                ))}
                <Input
                    ref={inputField}
                    type="text"
                    value={inputValue}
                    onBlur={(event) => {
                        findEmailAddress(event.currentTarget.value, true);
                    }}
                    onChange={(event) => findEmailAddress(event.currentTarget.value)}
                    onKeyDown={(event) => {
                        switch (event.key) {
                            case 'Enter':
                            case 'Tab':
                                event.preventDefault();
                                break;
                            case 'Backspace':
                                if (!event.currentTarget.value) {
                                    removeEmail(internalEmails.length - 1);
                                }
                                break;
                            default:
                        }
                    }}
                    onKeyUp={(event) => {
                        switch (event.key) {
                            case 'Enter':
                            case 'Tab':
                                findEmailAddress(event.currentTarget.value, true);
                                break;
                            default:
                        }
                    }}
                />
            </InputContainer>
            {!!value.length && (
                <ClearWrapper>
                    <IconButton
                        ariaLabel="Clear value"
                        onClick={() => {
                            onChange([]);
                            setInternalEmails([]);
                        }}
                    >
                        <Close />
                    </IconButton>
                </ClearWrapper>
            )}
            {!!error && typeof error !== 'boolean' && <InputErrorMessage id={errorId}>{error}</InputErrorMessage>}
        </Container>
    );
};

export default MultiEmail;
